import React, { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../../app/store';
import {
    getGenericUserInfo, getUserContents, getRouteProjects,
    getUserMusicalGenres, getUserInstrumentsProfile,
    createUserFellowship, getCurrentUserFelows, patchUserFellowship,
} from '../../../../app/features/sessions/sessionProfileNavigation';
import { ContentPlayer } from '../../../../app/components/audioPlayer/WaveSurfer/contentPlayer/ContentPlayer';
import { ProjectPlayer } from '../../../../app/components/audioPlayer/WaveSurfer/projectPlayer/ProjectPlayer';
import { format } from 'date-fns';
import ProjectDetailsModal from '../../../../app/components/modal/projectModal/ProjectDetails';
import ContentShowEdit from '../../../../app/components/modal/contentModal/ContentShowEdit';
import UserMusicalGenresModal from '../../../../app/components/modal/profileModals/musicalGenres/UserMusicalGenresModal';
import AddRemoveUserInstruments from '../../../../app/components/modal/profileModals/userInstruments/AddRemoveUserInstruments';
import UserInstrumentsModal from '../../../../app/components/modal/profileModals/userInstruments/UserInstrumentsModal';
import AddRemoveUserMusicalGenres from '../../../../app/components/modal/profileModals/musicalGenres/AddRemoveUserMusicalGenres';
import { Content, Fellowship, UserInstrument, UserMusicalGenre } from '../../../../app/#interfaces/interfaces';
import { selectDialogue } from '../../../../app/features/sessions/sessionDialogues';
import * as REQUEST_REQUIREMENTS from '../../../../app/api/requestRequirements';
import {
    Box, Chip, CircularProgress, Container,
    Divider, IconButton, Skeleton, Tab, Tabs, Typography,
} from '@mui/material';
import { UserAvatar } from '../../../../app/components/userAvatar/UserAvatar';
import GroupIcon from '@mui/icons-material/Group';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ChatIcon from '@mui/icons-material/Chat';
import PhoneIcon from '@mui/icons-material/Phone';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LanguageIcon from '@mui/icons-material/Language';
import VisibilityIcon from '@mui/icons-material/Visibility';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AddIcon from '@mui/icons-material/Add';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import MoreVertIcon from '@mui/icons-material/MoreVert';

const BG_MAIN        = '#15151a';
const BG_SECTION     = '#1c1c24';
const BG_CARD        = '#24242e';
const BRAND_RED      = '#c0392b';
const TEXT_PRIMARY   = '#f4f4f6';
const TEXT_SECONDARY = 'rgba(244,244,246,0.65)';
const BORDER_SUBTLE  = '1px solid rgba(255,255,255,0.08)';

export default function Profile() {
    const { userNicknameRoute } = useParams<string>();
    const navigate  = useNavigate();
    const dispatch  = useDispatch<AppDispatch>();

    const currentUser             = useSelector((state: RootState) => state.session.currentUser);
    const authHeaders             = useSelector((state: RootState) => state.session.authHeaders);
    const userMoreInfo            = useSelector((state: RootState) => state.sessionProfile.userMoreInfo);
    const userProfileRoute        = useSelector((state: RootState) => state.sessionProfile.userMoreInfo);
    const userMusicalGenres       = useSelector((state: RootState) =>
        state.sessionProfile.userMusicalGenres.filter((g: UserMusicalGenre) => g.selected === true));
    const userInstruments         = useSelector((state: RootState) =>
        state.sessionProfile.userInstruments.filter((i: UserInstrument) => i.selected === true));
    const userContents            = useSelector((state: RootState) => state.sessionProfile.userContents);
    const userProjects            = useSelector((state: RootState) => state.sessionProfile.userProjects);
    const loadingProfilePage      = useSelector((state: RootState) => state.sessionProfile.loadingProfilePage);
    const loadingMoreUserContents = useSelector((state: RootState) => state.sessionProfile.loadingMoreUserContents);
    const loadingMoreUserProjects = useSelector((state: RootState) => state.sessionProfile.loadingMoreUserProject);
    const selectedDialogues       = useSelector((state: RootState) => state.sessionDialogues.selectedDialogues);

    const userRouteIsFellow = useSelector((state: RootState) =>
        userNicknameRoute
            ? state.sessionProfile.currentUserFellows.some(
                (f: Fellowship) => f.fellowUser?.userMoreInfo?.nickname === userNicknameRoute)
            : false);

    const userRouteIsActiveFellow = useSelector((state: RootState) =>
        userNicknameRoute
            ? state.sessionProfile.currentUserFellows.some(
                (f: Fellowship) =>
                    f.fellowUser?.userMoreInfo?.nickname === userNicknameRoute && f.active === true)
            : false);

    const visibleMax                = 7;
    const remainingInstruments      = userInstruments.length - visibleMax;
    const remainingGenres           = userMusicalGenres.length - visibleMax;
    const isOwnProfile              = userNicknameRoute === currentUser?.nickname;

    const [activeTab, setActiveTab]                                       = useState(0);
    const [contentsPage, setContentsPage]                                 = useState(1);
    const [projectsPage, setProjectsPage]                                 = useState(1);
    const scrollLocked  = useRef(false);
    const sentinelRef   = useRef<HTMLDivElement>(null);
    const [musicalGenresModalOpen, setMusicalGenresModalOpen]             = useState(false);
    const [toggleModalDescriptionOpen, setToggleModalDescriptionOpen]     = useState(false);
    const [projectSelected, setProjectSelected]                           = useState<any>();
    const [showAddRemoveInstrumentsModal, setShowAddRemoveInstrumentsModal] = useState(false);
    const [showAddRemoveMusicalGenresModal, setShowAddRemoveMusicalGenresModal] = useState(false);
    const [showUserInstrumentsModal, setShowUserInstrumentsModal]         = useState(false);
    const [contentModalOpen, setContentModalOpen]                         = useState(false);
    const [selectedContent, setSelectedContent]                           = useState<Content | undefined>();

    const MemorizedContentPlayer = useMemo(() => React.memo(ContentPlayer), []);
    const MemorizedProjectPlayer = useMemo(() => React.memo(ProjectPlayer), []);

    useEffect(() => {
        if (!authHeaders || !userNicknameRoute) return;
        setContentsPage(1);
        setProjectsPage(1);

        async function fetchAll() {
            const profileRes = await dispatch(getGenericUserInfo({ authHeaders: authHeaders!, userNickname: userNicknameRoute! }));
            if (profileRes.meta.requestStatus !== 'fulfilled') {
                const PRIVATE_ROUTES = REQUEST_REQUIREMENTS.handlePrivateRoutes({
                    ROUTE_PARAMS: { userNickname: currentUser?.nickname },
                });
                navigate(PRIVATE_ROUTES.USER);
                return;
            }
            dispatch(getUserContents({ authHeaders: authHeaders!, routeParams: { userNickname: userNicknameRoute!, contentsPage: 1 } }));
            dispatch(getRouteProjects({ authHeaders: authHeaders!, urlParams: { userNickname: userNicknameRoute!, page: 1 } }));
            dispatch(getUserMusicalGenres({ headers: authHeaders!, urlParams: { userNickname: userNicknameRoute! } }));
            dispatch(getUserInstrumentsProfile({ headers: authHeaders!, urlParams: { userNickname: userNicknameRoute! } }));
            if (currentUser?.id) {
                dispatch(getCurrentUserFelows({ authHeaders: authHeaders!, urlParams: { currentUserId: currentUser.id } }));
            }
        }
        fetchAll();
    }, [userNicknameRoute]);

    useEffect(() => {
        if (contentsPage === 1 || !authHeaders || !userNicknameRoute) return;
        dispatch(getUserContents({ authHeaders, routeParams: { userNickname: userNicknameRoute, contentsPage } }));
    }, [contentsPage]);

    useEffect(() => {
        if (projectsPage === 1 || !authHeaders || !userNicknameRoute) return;
        dispatch(getRouteProjects({ authHeaders, urlParams: { userNickname: userNicknameRoute, page: projectsPage } }));
    }, [projectsPage]);

    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel) return;
        const observer = new IntersectionObserver((entries) => {
            if (!entries[0].isIntersecting || scrollLocked.current) return;
            scrollLocked.current = true;
            if (activeTab === 1) setContentsPage(p => p + 1);
            else if (activeTab === 0) setProjectsPage(p => p + 1);
        }, { threshold: 0 });
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [activeTab]);

    useEffect(() => {
        if (!loadingMoreUserContents && !loadingMoreUserProjects) {
            scrollLocked.current = false;
        }
    }, [loadingMoreUserContents, loadingMoreUserProjects]);

    function handleFormatDate(date: any) {
        return format(new Date(date), 'dd MMM yyyy HH:mm');
    }

    async function handleFellowship({ nickname }: { nickname: string | undefined }) {
        if (!authHeaders || !nickname) return;
        if (!userRouteIsFellow) {
            dispatch(createUserFellowship({ authHeaders, requestBodyData: { profileNickname: nickname } }));
        } else if (currentUser) {
            dispatch(patchUserFellowship({
                authHeaders,
                urlParams: { profileNickname: nickname, currentUserId: currentUser.id },
                patchBody: { active: !userRouteIsActiveFellow },
            }));
        }
    }

    function handleOpenNewDialogue(userDialogue: string | undefined) {
        if (userDialogue && !selectedDialogues.includes(userDialogue)) {
            dispatch(selectDialogue({ nickname: userDialogue }));
        }
    }

    const hasContactInfo = userMoreInfo.phoneNumber !== '' || userMoreInfo.email !== '';
    const hasLocation    = userMoreInfo.city !== '' || userMoreInfo.country !== '';

    // ── Skeleton ──
    if (loadingProfilePage) {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: BG_MAIN, py: { xs: 1.5, sm: 3 } }}>
                <Container maxWidth='lg'>
                    <Box sx={{ bgcolor: BG_SECTION, border: BORDER_SUBTLE, borderRadius: '12px', p: { xs: 2, sm: 3 }, mb: 3 }}>
                        <Box sx={{ display: 'flex', gap: 2, mb: 2.5 }}>
                            <Skeleton variant='circular' width={80} height={80} sx={{ bgcolor: 'rgba(255,255,255,0.08)', flexShrink: 0 }} />
                            <Box sx={{ flex: 1 }}>
                                <Skeleton variant='rounded' width={180} height={28} sx={{ bgcolor: 'rgba(255,255,255,0.08)', mb: 1 }} />
                                <Skeleton variant='rounded' width={130} height={20} sx={{ bgcolor: 'rgba(255,255,255,0.08)' }} />
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {Array.from({ length: 5 }).map((_, i) => (
                                <Skeleton key={i} variant='rounded' width={72} height={26} sx={{ bgcolor: 'rgba(255,255,255,0.07)' }} />
                            ))}
                        </Box>
                    </Box>
                    {Array.from({ length: 3 }).map((_, i) => (
                        <Box key={i} sx={{ bgcolor: BG_SECTION, border: BORDER_SUBTLE, borderRadius: '10px', p: 2, mb: 1.5 }}>
                            <Skeleton variant='rounded' width='45%' height={22} sx={{ bgcolor: 'rgba(255,255,255,0.08)', mb: 1.5 }} />
                            <Skeleton variant='rectangular' height={84} sx={{ bgcolor: 'rgba(255,255,255,0.06)', borderRadius: '6px' }} />
                        </Box>
                    ))}
                </Container>
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: BG_MAIN, py: { xs: 1.5, sm: 3 } }}>
            {/* ── Modals ── */}
            {showAddRemoveInstrumentsModal && (
                <AddRemoveUserInstruments userNickname={userNicknameRoute!} onClose={setShowAddRemoveInstrumentsModal} />
            )}
            {showAddRemoveMusicalGenresModal && (
                <AddRemoveUserMusicalGenres userNickname={userNicknameRoute!} onClose={setShowAddRemoveMusicalGenresModal} />
            )}
            {showUserInstrumentsModal && (
                <UserInstrumentsModal userNickname={userNicknameRoute!} onClose={setShowUserInstrumentsModal} />
            )}
            {musicalGenresModalOpen && (
                <UserMusicalGenresModal userNickname={userNicknameRoute!} onClose={setMusicalGenresModalOpen} />
            )}
            {toggleModalDescriptionOpen && (
                <ProjectDetailsModal
                    projectInfoAndUrls={projectSelected}
                    close={() => setToggleModalDescriptionOpen(false)}
                />
            )}
            {contentModalOpen && (
                <ContentShowEdit
                    contentObj={selectedContent}
                    onCloseClick={() => setContentModalOpen(false)}
                />
            )}

            <Container maxWidth='lg'>
                {/* ── Header card ── */}
                <Box sx={{ bgcolor: BG_SECTION, border: BORDER_SUBTLE, borderRadius: '12px', mb: 3, overflow: 'hidden' }}>

                    {/* Avatar + info + actions */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 1.5, sm: 2.5 }, px: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 3 }, pb: 2, flexWrap: 'wrap' }}>
                        <UserAvatar
                            src={userMoreInfo.avatarUrl}
                            size={80}
                            sx={{ border: '2px solid rgba(255,255,255,0.12)', width: { xs: 60, sm: 80 }, height: { xs: 60, sm: 80 } }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant='h5' fontWeight={700} color={TEXT_PRIMARY} sx={{ wordBreak: 'break-word', fontSize: { xs: '1.1rem', sm: '1.35rem', md: '1.5rem' } }}>
                                @{userProfileRoute?.nickname}
                            </Typography>
                            {(userProfileRoute?.name || userProfileRoute?.lastName) && (
                                <Typography variant='body2' color={TEXT_SECONDARY} sx={{ mt: 0.25 }}>
                                    {userProfileRoute?.name} {userProfileRoute?.lastName}
                                </Typography>
                            )}
                            {userMoreInfo.about && (
                                <Typography variant='body2' color={TEXT_SECONDARY} sx={{ mt: 1, fontStyle: 'italic', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                                    {userMoreInfo.about}
                                </Typography>
                            )}
                            {/* Info chips */}
                            {(hasContactInfo || hasLocation || userMoreInfo.website) && (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.5 }}>
                                    {userMoreInfo.phoneNumber && (
                                        <Chip
                                            icon={<PhoneIcon sx={{ fontSize: '13px !important' }} />}
                                            label={userMoreInfo.phoneNumber}
                                            size='small'
                                            sx={{ bgcolor: BG_CARD, color: TEXT_SECONDARY }}
                                        />
                                    )}
                                    {userMoreInfo.email && (
                                        <Chip
                                            icon={<MailOutlineIcon sx={{ fontSize: '13px !important' }} />}
                                            label={userMoreInfo.email}
                                            size='small'
                                            sx={{ bgcolor: BG_CARD, color: TEXT_SECONDARY }}
                                        />
                                    )}
                                    {hasLocation && (
                                        <Chip
                                            icon={<LocationOnIcon sx={{ fontSize: '13px !important' }} />}
                                            label={[userMoreInfo.city, userMoreInfo.state, userMoreInfo.country].filter(Boolean).join(', ')}
                                            size='small'
                                            sx={{ bgcolor: BG_CARD, color: TEXT_SECONDARY }}
                                        />
                                    )}
                                    {userMoreInfo.website && (
                                        <Chip
                                            icon={<LanguageIcon sx={{ fontSize: '13px !important' }} />}
                                            label={userMoreInfo.website}
                                            size='small'
                                            component='a'
                                            href={userMoreInfo.website.startsWith('http') ? userMoreInfo.website : `https://${userMoreInfo.website}`}
                                            target='_blank'
                                            rel='noopener noreferrer'
                                            clickable
                                            sx={{ bgcolor: BG_CARD, color: TEXT_SECONDARY, '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
                                        />
                                    )}
                                </Box>
                            )}
                        </Box>

                        {/* Action buttons */}
                        {!isOwnProfile && currentUser && (
                            <Box sx={{ display: 'flex', gap: 1, flexShrink: 0, flexWrap: 'wrap' }}>
                                <Chip
                                    icon={userRouteIsActiveFellow
                                        ? <GroupIcon sx={{ fontSize: '15px !important', color: '#fff !important' }} />
                                        : <PersonAddIcon sx={{ fontSize: '15px !important' }} />}
                                    label={userRouteIsActiveFellow ? 'Following' : userRouteIsFellow ? 'Pending' : 'Fellowship'}
                                    onClick={() => handleFellowship({ nickname: userNicknameRoute })}
                                    sx={{
                                        bgcolor: userRouteIsActiveFellow ? BRAND_RED : 'rgba(255,255,255,0.08)',
                                        color: userRouteIsActiveFellow ? '#fff' : TEXT_PRIMARY,
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        '&:hover': { bgcolor: userRouteIsActiveFellow ? '#a93226' : 'rgba(255,255,255,0.14)' },
                                    }}
                                />
                                <Chip
                                    icon={<ChatIcon sx={{ fontSize: '15px !important' }} />}
                                    label='Message'
                                    onClick={() => handleOpenNewDialogue(userNicknameRoute)}
                                    sx={{
                                        bgcolor: 'rgba(255,255,255,0.08)',
                                        color: TEXT_PRIMARY,
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        '&:hover': { bgcolor: 'rgba(255,255,255,0.14)' },
                                    }}
                                />
                            </Box>
                        )}
                    </Box>

                    <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />

                    {/* Instruments + Genres */}
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 }, py: { xs: 1.5, sm: 2 }, flexWrap: 'wrap' }}>
                        <Box>
                            <Typography variant='overline' color={TEXT_SECONDARY} sx={{ letterSpacing: 2, display: 'block', mb: 1 }}>
                                Instruments
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                                {userInstruments.slice(0, visibleMax).map((item: UserInstrument, i) => (
                                    <Chip key={i} label={item.instrument?.name} size='small'
                                        sx={{ bgcolor: BRAND_RED, color: '#fff', fontWeight: 600 }} />
                                ))}
                                {remainingInstruments > 0 && (
                                    <Chip label={`+${remainingInstruments}`} size='small'
                                        onClick={() => setShowUserInstrumentsModal(true)}
                                        sx={{ bgcolor: 'rgba(255,255,255,0.08)', color: TEXT_SECONDARY, cursor: 'pointer' }} />
                                )}
                                {isOwnProfile && (
                                    <Chip
                                        icon={<AddIcon sx={{ fontSize: '13px !important' }} />}
                                        label='edit'
                                        size='small'
                                        onClick={() => setShowAddRemoveInstrumentsModal(true)}
                                        sx={{ bgcolor: 'transparent', color: TEXT_SECONDARY, cursor: 'pointer', border: '1px dashed rgba(255,255,255,0.2)' }}
                                    />
                                )}
                            </Box>
                        </Box>

                        <Box>
                            <Typography variant='overline' color={TEXT_SECONDARY} sx={{ letterSpacing: 2, display: 'block', mb: 1 }}>
                                Musical Genres
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                                {userMusicalGenres.slice(0, visibleMax).map((g: UserMusicalGenre, i) => (
                                    <Chip key={i} label={g.musicGenre.description} size='small'
                                        sx={{ bgcolor: 'rgba(255,255,255,0.08)', color: TEXT_PRIMARY }} />
                                ))}
                                {remainingGenres > 0 && (
                                    <Chip label={`+${remainingGenres}`} size='small'
                                        onClick={() => setMusicalGenresModalOpen(true)}
                                        sx={{ bgcolor: 'rgba(255,255,255,0.08)', color: TEXT_SECONDARY, cursor: 'pointer' }} />
                                )}
                                {isOwnProfile && (
                                    <Chip
                                        icon={<AddIcon sx={{ fontSize: '13px !important' }} />}
                                        label='edit'
                                        size='small'
                                        onClick={() => setShowAddRemoveMusicalGenresModal(true)}
                                        sx={{ bgcolor: 'transparent', color: TEXT_SECONDARY, cursor: 'pointer', border: '1px dashed rgba(255,255,255,0.2)' }}
                                    />
                                )}
                            </Box>
                        </Box>
                    </Box>
                </Box>

                {/* ── Tabs ── */}
                <Tabs
                    value={activeTab}
                    onChange={(_, v) => setActiveTab(v)}
                    sx={{
                        mb: 2,
                        '& .MuiTab-root': { color: TEXT_SECONDARY, textTransform: 'none', fontWeight: 600 },
                        '& .Mui-selected': { color: TEXT_PRIMARY },
                        '& .MuiTabs-indicator': { bgcolor: BRAND_RED },
                    }}
                >
                    <Tab label='Projects' />
                    <Tab label='Contents' />
                </Tabs>

                {/* ── Projects ── */}
                {activeTab === 0 && (
                    <Box>
                        {userProjects.length === 0 && !loadingMoreUserProjects && (
                            <Box sx={{ py: 8, textAlign: 'center' }}>
                                <MusicNoteIcon sx={{ fontSize: 48, color: TEXT_SECONDARY, mb: 1 }} />
                                <Typography variant='body2' color={TEXT_SECONDARY}>No projects yet</Typography>
                            </Box>
                        )}
                        {userProjects.map((item, index) => (
                            <Box key={index} sx={{ bgcolor: BG_SECTION, border: BORDER_SUBTLE, borderRadius: '10px', mb: 1.5, overflow: 'hidden' }}>
                                <Box sx={{ px: 2, pt: 1.5, pb: item.masterFiles?.length ? 0 : 1.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                        <Typography variant='h6' fontWeight={700} color={TEXT_PRIMARY} noWrap sx={{ flex: 1, fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.25rem' } }}>
                                            {item.project.title}
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <VisibilityIcon sx={{ fontSize: 15, color: TEXT_SECONDARY }} />
                                                <Typography variant='caption' color={TEXT_SECONDARY}>{item.project.nVisits}</Typography>
                                            </Box>
                                            <IconButton
                                                size='small'
                                                onClick={() => { setProjectSelected(item); setToggleModalDescriptionOpen(true); }}
                                                sx={{ color: TEXT_SECONDARY, '&:hover': { color: TEXT_PRIMARY } }}
                                            >
                                                <InfoOutlinedIcon fontSize='small' />
                                            </IconButton>
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                                        <Chip label={`Useful: ${item.project.nUsefulContents}`} size='small'
                                            sx={{ bgcolor: 'rgba(255,255,255,0.06)', color: TEXT_SECONDARY, fontSize: '0.65rem' }} />
                                        <Chip label={`Staging: ${item.project.nUselessContents}`} size='small'
                                            sx={{ bgcolor: 'rgba(255,255,255,0.06)', color: TEXT_SECONDARY, fontSize: '0.65rem' }} />
                                        <Typography variant='caption' color='rgba(244,244,246,0.3)'>
                                            {handleFormatDate(item.project.updatedAt)}
                                        </Typography>
                                    </Box>
                                </Box>
                                {item.masterFiles && item.masterFiles.length > 0 && (
                                    <Box sx={{ px: 2, pb: 1.5 }}>
                                        <MemorizedProjectPlayer fileUrl={`${item.masterFiles[item.masterFiles.length - 1].signedUrl}`} />
                                    </Box>
                                )}
                            </Box>
                        ))}
                        {loadingMoreUserProjects && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                <CircularProgress size={28} sx={{ color: BRAND_RED }} />
                            </Box>
                        )}
                        <Box ref={sentinelRef} sx={{ height: 1 }} />
                    </Box>
                )}

                {/* ── Contents ── */}
                {activeTab === 1 && (
                    <Box>
                        {userContents.length === 0 && !loadingMoreUserContents && (
                            <Box sx={{ py: 8, textAlign: 'center' }}>
                                <MusicNoteIcon sx={{ fontSize: 48, color: TEXT_SECONDARY, mb: 1 }} />
                                <Typography variant='body2' color={TEXT_SECONDARY}>No contents yet</Typography>
                            </Box>
                        )}
                        {userContents.map((item, index) => (
                            <Box key={index} sx={{ bgcolor: BG_SECTION, border: BORDER_SUBTLE, borderRadius: '10px', px: 2, pt: 1.5, pb: 1.5, mb: 1.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                                    <Typography variant='body2' fontWeight={600} color={TEXT_PRIMARY} noWrap sx={{ flex: 1 }}>
                                        {item.title}
                                    </Typography>
                                    <IconButton
                                        size='small'
                                        sx={{ color: TEXT_SECONDARY, ml: 1, flexShrink: 0, '&:hover': { color: TEXT_PRIMARY } }}
                                        onClick={() => { setSelectedContent(item); setContentModalOpen(true); }}
                                    >
                                        <MoreVertIcon fontSize='small' />
                                    </IconButton>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{ width: 88, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                                        <Chip
                                            label={item.instrument?.name ?? 'other'}
                                            size='small'
                                            sx={{ bgcolor: BRAND_RED, color: '#fff', fontWeight: 600, maxWidth: 88 }}
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <MemorizedContentPlayer fileUrl={`${item.fileUrl}`} />
                                    </Box>
                                </Box>
                            </Box>
                        ))}
                        {loadingMoreUserContents && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                <CircularProgress size={28} sx={{ color: BRAND_RED }} />
                            </Box>
                        )}
                        <Box ref={sentinelRef} sx={{ height: 1 }} />
                    </Box>
                )}
            </Container>
        </Box>
    );
}
